import { apiBackground } from './api'

/**
 * LOT #142 / E-20 — client du module backend `twilio-balance`.
 *
 * Tout est calculé SERVEUR : le frontend ne compare aucun seuil, il affiche
 * l'état reçu. Le montant n'est servi qu'au super-admin ; les autres rôles
 * reçoivent `{ state }` seul, SUSPENDED ou OK (le solde ne les concerne pas —
 * décision utilisateur #142).
 */

export type TwilioBalanceState = 'OK' | 'WARN' | 'CRITICAL' | 'SUSPENDED' | 'UNKNOWN'

export interface TwilioBalanceThresholds {
  warn: number
  critical: number
}

/**
 * Réponse de GET /api/twilio-balance. Les champs au-delà de `state` n'existent
 * que pour le super-admin (ADMIN du tenant Système).
 */
export interface TwilioBalanceStatus {
  state: TwilioBalanceState
  /** Solde lu, null si illisible (suspendu, indisponible). Super-admin seulement. */
  balance?: number | null
  /** Devise ISO 4217 du compte (`USD`). Super-admin seulement. */
  currency?: string | null
  /** Statut Twilio du compte (`active`, `suspended`…). Super-admin seulement. */
  accountStatus?: string | null
  /** Horodatage ISO du dernier relevé tenté. Super-admin seulement. */
  checkedAt?: string | null
  thresholds?: TwilioBalanceThresholds
  /** `twilio`, `fake` (banc) ou `none` (client non configuré). Super-admin seulement. */
  source?: 'twilio' | 'fake' | 'none'
  error?: string | null
}

/** Code STABLE du 429 rendu par POST /refresh sous throttle (1/min). */
export const TWILIO_BALANCE_REFRESH_THROTTLED = 'TWILIO_BALANCE_REFRESH_THROTTLED'

/**
 * Console Twilio → Billing. Le geste de recharge (et l'auto-recharge) se fait
 * là, jamais dans le CRM : le lien est l'action du bandeau super-admin.
 */
export const TWILIO_BILLING_CONSOLE_URL =
  'https://console.twilio.com/us1/billing/manage-billing/billing-overview'

/** Reconnaît le 429 de throttle dans une erreur axios. */
export function isRefreshThrottledError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response
  return response?.status === 429 || response?.data?.code === TWILIO_BALANCE_REFRESH_THROTTLED
}

class TwilioBalanceService {
  private static readonly BASE_PATH = '/twilio-balance'

  /**
   * État courant — alimente le bandeau du châssis et la carte super-admin.
   *
   * `apiBackground` (en-tête `X-Background-Request`) : ni spinner global ni
   * toast d'erreur de l'intercepteur — un sondage toutes les 5 min ne doit
   * jamais faire clignoter l'écran ni signaler un réseau absent que
   * l'OfflineDetector dit déjà.
   */
  static async getStatus(): Promise<TwilioBalanceStatus> {
    const response = await apiBackground.get(this.BASE_PATH)
    return response.data
  }

  /**
   * Relevé forcé (super-admin). 429 si moins d'une minute depuis le précédent.
   *
   * En arrière-plan AUSSI : l'intercepteur global toaste en rouge toute erreur
   * non marquée (`api.ts`), et le 429 est un cas NOMINAL que la carte traite
   * elle-même (message d'information) — deux émetteurs = doublon vu en
   * recette (#142, D2). Corps `{}` et non `null` (piège « "null" is not valid
   * JSON » du registre).
   */
  static async refresh(): Promise<TwilioBalanceStatus> {
    const response = await apiBackground.post(`${this.BASE_PATH}/refresh`, {})
    return response.data
  }
}

export default TwilioBalanceService
